const Common = (function () {
  /* global DEV_ENV */
  const BACKEND_URL = DEV_ENV ? 'https://supercup.nede.cz/api' : './api';

  const UNAUTHORIZED = 401;
  const NOT_FOUND = 404;
  const IM_A_TEAPOT = 418;

  const DATE_PATTERN = /^([0-9]{1,2})[./-] ?([0-9]{1,2})[./-] ?([0-9]{2}|[0-9]{4})$/;
  const TIME_PATTERN = /^([0-9]{1,2}):([0-9]{2})$/;
  const DB_FULL_DATE_PATTERN = /^([0-9]{4})([0-9]{2})([0-9]{2})([0-9]{2})([0-9]{2})$/;

  /**
   * Adds zeros in front of number (e.g. converts 3 to 03)
   * @param {number} number
   * @param {number} length
   * @returns {string}
   */
  function addZeros (number, length = 2) {
    if (number === undefined) return '';

    return number.toString().padStart(length, 0);
  }

  /**
   * Checks if passwords match and are valid
   * @param {string} password
   * @param {string} confirmPassword
   * @throws
   */
  function checkPassword (password, confirmPassword) {
    const passwordMinLength = 8;
    const passwordMaxLength = 64;

    if (password !== confirmPassword) {
      throw new Error(JSON.stringify({
        cs: 'Hesla se neshodují.',
        en: 'Passwords do not match.',
      }));
    }

    if (password.length < passwordMinLength) {
      throw new Error(JSON.stringify({
        cs: `Heslo nesmí být kratší než ${passwordMinLength} znaků.`,
        en: `Password must not be shorter than ${passwordMinLength} characters.`,
      }));
    }

    if (password.length > passwordMaxLength) {
      throw new Error(JSON.stringify({
        cs: `Heslo nesmí být delší než ${passwordMaxLength} znaků.`,
        en: `Password must be no longer than ${passwordMaxLength} characters.`,
      }));
    }
  }

  /**
   * Checks min length, max length, min, max, pattern and values of the value
   * @param {unknown} value
   * @param {unknown{}} properties
   * @throws
   */
  function checkProperties (value, properties) {
    if (properties.minLength !== undefined && value.length < properties.minLength) {
      throw new Error(JSON.stringify({
        cs: `${properties.label.cs} "${value.toString().replaceAll('"', '&quot;')}" nesmí být kratší než ${properties.minLength} znaky.`,
        en: `${properties.label.en} "${value.toString().replaceAll('"', '&quot;')}" must not be shorter than ${properties.minLength} characters.`,
      }));
    }

    if (properties.maxLength !== undefined && value.length > properties.maxLength) {
      throw new Error(JSON.stringify({
        cs: `${properties.label.cs} "${value.toString().replaceAll('"', '&quot;')}" nesmí být delší než ${properties.maxLength} znaků.`,
        en: `${properties.label.en} "${value.toString().replaceAll('"', '&quot;')}" must not be longer than ${properties.maxLength} characters.`,
      }));
    }

    if (properties.min !== undefined && value < properties.min) {
      throw new Error(JSON.stringify({
        cs: `${properties.label.cs} "${value.toString().replaceAll('"', '&quot;')}" nesmí být menší než ${properties.min}.`,
        en: `${properties.label.en} "${value.toString().replaceAll('"', '&quot;')}" must not be less than ${properties.min}.`,
      }));
    }

    if (properties.max !== undefined && value > properties.max) {
      throw new Error(JSON.stringify({
        cs: `${properties.label.cs} "${value.toString().replaceAll('"', '&quot;')}" nesmí být větší než ${properties.max}.`,
        en: `${properties.label.en} "${value.toString().replaceAll('"', '&quot;')}" must not be greater than ${properties.max}.`,
      }));
    }

    if (properties.pattern !== undefined && !properties.pattern.test(value)) {
      throw new Error(JSON.stringify({
        cs: `${properties.label.cs} "${value.toString().replaceAll('"', '&quot;')}" ${properties.patternErrorMessage.cs}.`,
        en: `${properties.label.en} "${value.toString().replaceAll('"', '&quot;')}" ${properties.patternErrorMessage.en}.`,
      }));
    }

    if (properties.values) {
      value.forEach(element => {
        if (!properties.values.includes(element)) {
          throw new Error(JSON.stringify({
            cs: `${properties.labelSingular.cs} "${element.toUpperCase()}": Neočekávaná chyba. \`E111\``,
            en: `${properties.labelSingular.en} "${element.toUpperCase()}": Unexpected error. \`E111\``,
          }));
        }
      });
    }
  }

  /**
   * Attempts to convert date to DD.MM.YYYY and then checks if is valid
   * @param {string} date
   * @param {{ cs: string, en: string }} label
   * @returns {string}
   * @throws
   */
  function convertAndCheckDate (date, label) {
    date = formatDate(date);

    const day = formatNumber(date.split('.')[0]);
    const month = formatNumber(date.split('.')[1]);
    const year = formatNumber(date.split('.')[2]);

    const minDay = 1;
    const maxDay = getDaysInMonth(month, year);
    const minMonth = 1;
    const maxMonth = 12;
    const minYear = 1900;
    const maxYear = 2200;

    if (day < minDay || day > maxDay ||
      month < minMonth || month > maxMonth ||
      year < minYear || year > maxYear) {
      throw new Error(JSON.stringify({
        cs: `${label.cs} "${date.replaceAll('"', '&quot;')}" není platné datum.`,
        en: `${label.en} "${date.replaceAll('"', '&quot;')}" is not a valid date.`,
      }));
    }

    return date;

    /**
     * Gets days in given month
     * @param {number} month
     * @param {number} year
     * @returns {number}
     */
    function getDaysInMonth (month, year) {
      /* eslint-disable no-magic-numbers */
      if ([1, 3, 5, 7, 8, 10, 12].includes(month)) return 31;
      else if ([4, 6, 9, 11].includes(month)) return 30;
      else if ([2].includes(month)) {
        if (year % 4 === 0 && (year % 100 !== 0 || (year % 100 === 0 && year % 400 === 0))) {
          return 29;
        } else {
          return 28;
        }
      }
      /* eslint-enable no-magic-numbers */
    }
  }

  /**
   * Attempts to convert time to HH:MM and then checks if is valid
   * @param {string} time
   * @param {{ cs: string, en: string }} label
   * @returns {string}
   * @throws
   */
  function convertAndCheckTime (time, label) {
    time = formatTime(time);

    const hours = formatNumber(time.split(':')[0]);
    const minutes = formatNumber(time.split(':')[1]);

    const minHours = 0;
    const maxHours = 23;
    const minMinutes = 0;
    const maxMinutes = 59;

    if (hours < minHours || hours > maxHours ||
      minutes < minMinutes || minutes > maxMinutes) {
      throw new Error(JSON.stringify({
        cs: `${label.cs} "${time.replaceAll('"', '&quot;')}" není platný čas.`,
        en: `${label.cs} "${time.replaceAll('"', '&quot;')}" is not a valid time.`,
      }));
    }

    return time;
  }

  /**
   * Attempts to convert to the given type and then checks the type of the value
   * @param {unknown} value
   * @param {string} type
   * @param {{ cs: string, en: string }} label
   * @returns {unknown}
   * @throws
   */
  function convertAndCheckType (value, type, label) {
    // E101 - string
    if (type === 'string') {
      if (typeof value === 'string') return formatString(value);

      throw new Error(JSON.stringify({
        cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}": Neočekávaná chyba. \`E101\``,
        en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}": Unexpected error. \`E101\``,
      }));
    // E102 - object
    } else if (type === 'object') {
      if (typeof value === 'object') return value;

      throw new Error(JSON.stringify({
        cs: `${label.cs}: Neočekávaná chyba. \`E102\``,
        en: `${label.en}: Unexpected error. \`E102\``,
      }));
    // E103 - array
    } else if (type === 'array') {
      value = typeof value === 'object' ? value : value.split(',');

      if (!Array.isArray(value)) {
        throw new Error(JSON.stringify({
          cs: `${label.cs}: Neočekávaná chyba. \`E103\``,
          en: `${label.en}: Unexpected error. \`E103\``,
        }));
      }

      return value;
    // E104 - number
    } else if (type === 'number') {
      value = !isNaN(formatNumber(value)) ? formatNumber(value) : value;

      if (typeof value !== 'number') {
        throw new Error(JSON.stringify({
          cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}" musí být číslo.`,
          en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}" must be a number.`,
        }));
      }

      return value;
    // E105 - integer
    } else if (type === 'integer') {
      value = !isNaN(formatNumber(value)) ? formatNumber(value) : value;

      if (typeof value !== 'number') {
        throw new Error(JSON.stringify({
          cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}" musí být číslo.`,
          en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}" must be a number.`,
        }));
      } else if (!Number.isInteger(value)) {
        throw new Error(JSON.stringify({
          cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}" nesmí být desetinné číslo.`,
          en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}" must not be a decimal number.`,
        }));
      }

      return value;
    // E106 - name
    } else if (type === 'name') {
      if (typeof value === 'string') return formatName(formatString(value));

      throw new Error(JSON.stringify({
        cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}": Neočekávaná chyba. \`E106\``,
        en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}": Unexpected error. \`E106\``,
      }));
    // E107 - date
    } else if (type === 'date') {
      if (typeof value === 'string') {
        value = formatString(value);

        if (!DATE_PATTERN.test(value)) {
          throw new Error(JSON.stringify({
            cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}" není v požadovaném formátu (DD.MM.RRRR).`,
            en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}" is not in the required format (DD.MM.YYYY).`,
          }));
        }

        return convertAndCheckDate(value, label);
      }

      throw new Error(JSON.stringify({
        cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}": Neočekávaná chyba. \`E107\``,
        en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}": Unexpected error. \`E107\``,
      }));
    // E108 - time
    } else if (type === 'time') {
      if (typeof value === 'string') {
        value = formatString(value);

        if (!TIME_PATTERN.test(value)) {
          throw new Error(JSON.stringify({
            cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}" není v požadovaném formátu (DD.MM.RRRR).`,
            en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}" is not in the required format (DD.MM.YYYY).`,
          }));
        }

        return convertAndCheckTime(value, label);
      }

      throw new Error(JSON.stringify({
        cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}": Neočekávaná chyba. \`E108\``,
        en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}": Unexpected error. \`E108\``,
      }));
    // E109 - boolean
    } else if (type === 'boolean') {
      if (typeof value === 'boolean') return value;

      throw new Error(JSON.stringify({
        cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}": Neočekávaná chyba. \`E109\``,
        en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}": Unexpected error. \`E109\``,
      }));
    // E100
    } else {
      throw new Error(JSON.stringify({
        cs: `${label.cs} "${value.toString().replaceAll('"', '&quot;')}": Neočekávaná chyba. \`E100\``,
        en: `${label.en} "${value.toString().replaceAll('"', '&quot;')}": Unexpected error. \`E100\``,
      }));
    }
  }

  /**
   * Converts date in database format (YYYYMMDDHHMM) to human readable format DD.MM.YYYY
   * @param {string} fullDate
   * @returns {string}
   */
  function convertDbDate (fullDate) {
    fullDate = fullDate.toString().match(DB_FULL_DATE_PATTERN);

    const year = 1;
    const month = 2;
    const day = 3;

    return `${fullDate[day]}.${fullDate[month]}.${fullDate[year]}`;
  }

  /**
   * Converts full date in database format (YYYYMMDDHHMM) to human readable format (DD.MM.YYYY (HH:MM))
   * @param {string} fullDate
   * @returns {string}
   */
  function convertDbFullDate (fullDate) {
    fullDate = fullDate.toString().match(DB_FULL_DATE_PATTERN);

    const year = 1;
    const month = 2;
    const day = 3;
    const hours = 4;
    const minutes = 5;

    return `${fullDate[day]}.${fullDate[month]}.${fullDate[year]} (${fullDate[hours]}:${fullDate[minutes]})`;
  }

  /**
   * Converts time in database format (YYYYMMDDHHMM) to human readable format HH:MM
   * @param {string} fullDate
   * @returns {string}
   */
  function convertDbTime (fullDate) {
    fullDate = fullDate.toString().match(DB_FULL_DATE_PATTERN);

    const hours = 4;
    const minutes = 5;

    return `${fullDate[hours]}:${fullDate[minutes]}`;
  }

  /**
   * Makes a deep copy of an object
   * @param {unknown{}} object
   * @returns {unknown{}}
   */
  function deepCopy (object) {
    return JSON.parse(JSON.stringify(object));
  }

  /**
   * Escapes HTML and converts quotes to code
   * @param {string} string
   * @returns {string}
   */
  function escapeHtml (string) {
    if (string === undefined) return '';

    string = string.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');

    while (string.match(/".+"/)) {
      string = string.replace(/"/, '<q>').replace(/"/, '</q>');
    }

    while (string.match(/`.+`/)) {
      string = string.replace(/`/, '<p>').replace(/`/, '</p>');
    }

    return string;
  }

  /**
   * Converts string with markdown to HTML string
   * @param {string} string
   * @param {boolean} imageAndVideo
   * @param {boolean} preview
   * @returns {string}
   */
  function escapeMarkdown (string, imageAndVideo = false, preview = false) {
    if (string === undefined) return '';

    string = string.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');

    while (string.match(/\*{2}.+?\*{2}/)) {
      string = string.replace(/\*{2}(.+?)\*{2}/, '<b>$1</b>');
    }

    while (string.match(/_{2}.+?_{2}/)) {
      string = string.replace(/_{2}(.+?)_{2}/, '<b>$1</b>');
    }

    while (string.match(/\*.+?\*/)) {
      string = string.replace(/\*(.+?)\*/, '<i>$1</i>');
    }

    while (string.match(/_.+?_/)) {
      string = string.replace(/_(.+?)_/, '<i>$1</i>');
    }

    while (string.match(/# .+?<br>/)) {
      string = string.replace(/# (.+?)<br>/, '<h1 id=$1>$1</h1>');
    }

    if (imageAndVideo) {
      while (string.match(/!\[[^\]]+\]\([^)]+\)/)) {
        const match = string.match(/!\[([^\]]+)\]\(([^)]+)\)/);

        if (!preview || !match[2].match(/p[0-9]+/)) {
          string = string.replace(
            /!\[[^\]]+\]\([^)]+\)/,
            `
            <div class='image'>
              <img class='image-content' src='img/photogallery/${match[2]}.jpg' alt='${match[1]}'/>
              <div class='image-label'> ${match[1]} </div>
            </div>
            `,
          );
        } else {
          string = string.replace(
            /!\[[^\]]+\]\([^)]+\)/,
            `
            <div class='image'>
              <img class='image-content' src='https://fotobanka.baseball.cz/img/s/v-10/${match[2]}.jpg' alt='${match[1]}'/>
              <div class='image-label'> ${match[1]} </div>
            </div>
            `,
          );
        }
      }

      while (string.match(/\?\[[^\]]+\]\([^)]+\)/)) {
        const match = string.match(/\?\[([^\]]+)\]\(([^)]+)\)/);

        if (!match[2].match(/s-[a-zA-Z0-9-]+/)) {
          string = string.replace(
            /\?\[[^\]]+\]\([^)]+\)/,
            `
            <div class='video'>
              <iframe class='video-content' src='https://www.youtube-nocookie.com/embed/${match[2]}' title='${match[1]}' frameborder='0' allowfullscreen></iframe>
              <div class='video-label'> ${match[1]} </div>
            </div>
            `,
          );
        } else {
          string = string.replace(
            /\?\[[^\]]+\]\([^)]+\)/,
            `
            <div class='video'>
              <iframe class='video-content' src='https://embed.staylive.tv/video/${match[2]}?&autoplay=false' title='${match[1]}' frameborder='0' allowfullscreen></iframe>
              <div class='video-label'> ${match[1]} </div>
            </div>
            `,
          );
        }
      }

      while (string.match(/@\[[^\]]+\]\([^)]+\)/)) {
        const match = string.match(/@\[([^\]]+)\]\(([^)]+)\)/);

        string = string.replace(
          /@\[[^\]]+\]\([^)]+\)/,
          `
          <div class='tour'>
            <iframe class='tour-content' src='https://kuula.co/share/collection/${match[2]}?vr=1&sd=1&autorotate=0.20&initload=0' title='${match[1]}' frameborder='0' allowfullscreen allow='xr-spatial-tracking; gyroscope; accelerometer;' scrolling='no'></iframe>
            <div class='tour-label'> ${match[1]} </div>
          </div>
          `,
        );
      }
    }

    while (string.match(/\[[^\]]+\]\([^)]+\)/)) {
      const match = string.match(/\[([^\]]+)\]\(([^)]+)\)/);

      string = string.replace(/\[[^\]]+\]\([^)]+\)/, `<a href='${match[2]}'>${match[1]}</a>`);
    }

    return string;
  }

  /**
   * Converts the first character to uppercase and other to lowercase
   * @param {string} string
   * @returns {string}
   */
  function firstToUpperCase (string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }

  /**
   * Formats date from any valid format to DD.MM.YYYY
   * @param {string} date
   * @returns {string}
   */
  function formatDate (date) {
    date = date.match(DATE_PATTERN);

    const day = 1;
    const month = 2;
    const year = 3;

    return addZeros(date[day]) + '.' + addZeros(date[month]) + '.' + getFullYear(date[year]);
  }

  /**
   * Formats date from any valid format to DD.MM.
   * @param {string} date
   * @returns {string}
   */
  function formatDateShort (date) {
    date = date.match(DATE_PATTERN);

    const day = 1;
    const month = 2;

    return addZeros(date[day]) + '.' + addZeros(date[month]) + '.';
  }

  /**
   * Formats date from any valid format to database format (YYYYMMDD)
   * @param {string} date
   * @returns {string}
   */
  function formatDbDate (date) {
    date = date.match(DATE_PATTERN);

    const day = 1;
    const month = 2;
    const year = 3;

    return getFullYear(date[year]) + addZeros(date[month]) + addZeros(date[day]);
  }

  /**
   * Formats time from any valid format to database format (HHMM)
   * @param {string} time
   * @returns {string}
   */
  function formatDbTime (time) {
    time = time.match(TIME_PATTERN);

    const hours = 1;
    const minutes = 2;

    return addZeros(time[hours]) + addZeros(time[minutes]);
  }

  /**
   * Formats name with spaces to name without spaces with capital first character
   * @param {string} name
   * @returns {string}
   */
  function formatName (name) {
    return firstToUpperCase(formatString(name));
  }

  /**
    * Formats string with number value to number
    * @param {unknown} number
    * @returns {number}
    */
  function formatNumber (number) {
    return Number(number);
  }

  /**
   * Formats string without outer spaces and multiple inner spaces
   * @param {string} string
   * @returns {string}
   */
  function formatString (string) {
    return string.replace(/  +/g, ' ').trim();
  }

  /**
   * Formats time from any valid format to HH:MM
   * @param {string} time
   * @returns {string}
   */
  function formatTime (time) {
    time = time.match(TIME_PATTERN);

    const hours = 1;
    const minutes = 2;

    return addZeros(time[hours]) + ':' + addZeros(time[minutes]);
  }

  /**
   * Gets date
   * @returns {string}
   */
  function getDate () {
    const date = new Date();

    const day = addZeros(date.getDate());
    const month = addZeros(date.getMonth() + 1);
    const year = date.getFullYear();

    return day + '.' + month + '.' + year;
  }

  /**
   * Gets day of week
   * @param {string} date
   * @returns {unknown{}}
   */
  function getDayOfWeek (date) {
    date = date.match(DATE_PATTERN);

    const day = 1;
    const month = 2;
    const year = 3;

    const weekday = new Date(date[year], date[month] - 1, date[day]).getDay();

    const DAYS_CS = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];
    const DAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return { cs: DAYS_CS[weekday], en: DAYS_EN[weekday] };
  }

  /**
   * Gets date for database
   * @returns {string}
   */
  function getDbDate () {
    const date = new Date();

    const year = date.getFullYear();
    const month = addZeros(date.getMonth() + 1);
    const day = addZeros(date.getDate());

    return year + month + day;
  }

  /**
   * Gets time for database
   * @returns {string}
   */
  function getDbTime () {
    const date = new Date();

    const hours = addZeros(date.getHours());
    const minutes = addZeros(date.getMinutes());

    return hours + minutes;
  }

  /**
   * Gets full year from two digit year (e.g. converts 93 to 1993)
   * @param {string} year
   * @returns {string}
   */
  function getFullYear (year) {
    const fullYearLength = 4;

    if (year.length === fullYearLength) return year;

    const century = year > (new Date().getFullYear()).toString().substring(2, fullYearLength) ? '19' : '20';

    return year.toString().padStart(fullYearLength, century);
  }

  /**
   * Gets ordinal suffix of number
   * @param {number} number
   * @returns {string}
   */
  function getOrdinal (number) {
    /* eslint-disable no-magic-numbers */
    if (number % 10 === 1 && number % 100 !== 11) return 'st';
    if (number % 10 === 2 && number % 100 !== 12) return 'nd';
    if (number % 10 === 3 && number % 100 !== 13) return 'rd';
    return 'th';
    /* eslint-enable no-magic-numbers */
  }

  /**
   * Remove diacritics from string
   * @param {string} string
   * @returns {string}
   */
  function removeDiacritics (string) {
    return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Sorts array of objects by given key
   * @param {unknown{}[]} array
   * @param {string[]} keys
   * @param {boolean} descending
   * @returns {unknown{}[]}
   */
  function sortArrayOfObjectsByKey (array, keys, descending = false) {
    keys = unique(keys);

    return array.sort((objectA, objectB) => {
      for (let i = 0; i < keys.length; i++) {
        let a, b;

        if (keys[i].split('.').length === 1) {
          a = toNumber(objectA[keys[i]]);
          b = toNumber(objectB[keys[i]]);
        } else {
          a = toNumber(objectA[keys[i].split('.')[0]][keys[i].split('.')[1]]);
          b = toNumber(objectB[keys[i].split('.')[0]][keys[i].split('.')[1]]);
        }

        if (typeof a === 'string') a = removeDiacritics(a);
        if (typeof b === 'string') b = removeDiacritics(b);

        if (keys[i].toLowerCase().match(/date/)) {
          if (a === 'DD.MM.RRRR' && b === 'DD.MM.RRRR') continue;
          else if (a === 'DD.MM.RRRR') return !(descending && i === 0) ? -1 : 1;
          else if (b === 'DD.MM.RRRR') return !(descending && i === 0) ? 1 : -1;

          a = a.match(DATE_PATTERN);
          b = b.match(DATE_PATTERN);

          const day = 1;
          const month = 2;
          const year = 3;

          a = a[year] + a[month] + a[day];
          b = b[year] + b[month] + b[day];
        }

        if (a < b) return !(descending && i === 0) ? -1 : 1;
        if (a > b) return !(descending && i === 0) ? 1 : -1;
      }

      return 0;
    });
  }

  /**
   * Removes diacritics and converts to lower case
   * @param {string} string
   * @returns {string}
   */
  function toCode (string) {
    if (string === undefined) return '';

    return removeDiacritics(string).toLowerCase();
  }

  /**
   * Converts unknow value (string or number) to number if is possible
   * @param {unknown} value
   * @returns {unknown}
   */
  function toNumber (value) {
    if (!isNaN(Number(value))) value = Number(value);

    return value;
  }

  /**
   * Makes array unique
   * @param {unknown[]} array
   * @returns {unknown[]}
   */
  function unique (array) {
    return [...new Set(array)];
  }

  return {
    BACKEND_URL,
    UNAUTHORIZED,
    NOT_FOUND,
    IM_A_TEAPOT,
    DATE_PATTERN,
    TIME_PATTERN,
    DB_FULL_DATE_PATTERN,
    addZeros,
    checkPassword,
    checkProperties,
    convertAndCheckDate,
    convertAndCheckTime,
    convertAndCheckType,
    convertDbDate,
    convertDbFullDate,
    convertDbTime,
    deepCopy,
    escapeHtml,
    escapeMarkdown,
    firstToUpperCase,
    formatDate,
    formatDateShort,
    formatDbDate,
    formatDbTime,
    formatName,
    formatNumber,
    formatString,
    formatTime,
    getDate,
    getDayOfWeek,
    getDbDate,
    getDbTime,
    getFullYear,
    getOrdinal,
    removeDiacritics,
    sortArrayOfObjectsByKey,
    toCode,
    toNumber,
    unique,
  };
})();

export default Common;
